import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { useDeviceLayout } from '@vio/atlas/context/deviceLayout';
import { Flex } from '@vio/atlas/helpers/Flex';
import { MobileFiltersButton } from 'components/Filters/MobileFiltersButton/MobileFiltersButton';
import { getAllFilterKeys, getOrderedActiveQuickFilters, IQuickFilter, isFacetValid, isQuickFilterActive } from 'components/Filters/utils';
import { AllFiltersOverlay } from 'components/FiltersOverlay/Desktop/FiltersOverlayDesktop';
import { BreakfastPill } from 'components/FiltersPill/BreakfastPill';
import { FacilityQuickFilterPill } from 'components/FiltersPill/FacilityQuickFilterPill/FacilityQuickFilterPill';
import { PropertyTypeFilterPill } from 'components/FiltersPill/PropertyTypeFilterPill/PropertyTypeFilterPill';
import { ReviewScorePill } from 'components/FiltersPill/ReviewScorePill/ReviewScorePill';
import { ThemeQuickFilterPill } from 'components/FiltersPill/ThemeQuickFilterPill/ThemeQuickFilterPill';
import { FacilitiesFilterPopover } from 'components/FiltersPopovers/FacilitiesFilterPopover/FacilitiesFilterPopover';
import { FiltersScrollWrapper } from 'components/FiltersPopovers/FiltersScrollWrapper/FiltersScrollWrapper';
import { FreeCancellationPill } from 'components/FiltersPopovers/FreeCancellationPill/FreeCancellationPill';
import { MealsFilterPopover } from 'components/FiltersPopovers/MealsFilterPopover/MealsFilterPopover';
import { PayLaterPill } from 'components/FiltersPopovers/PayLaterPill/PayLaterPill';
import { PriceFilterPopover } from 'components/FiltersPopovers/PriceFilterPopover/PriceFilterPopover';
import { PropertyTypeFilterPopover } from 'components/FiltersPopovers/PropertyTypeFilterPopover';
import { ReviewScoreFilterPopover } from 'components/FiltersPopovers/ReviewScoreFilterPopover/ReviewScoreFilterPopover';
import { StarRatingFilterPopover } from 'components/FiltersPopovers/StarRatingFilterPopover/StarRatingFilterPopover';
import { ThemesFilterPopover } from 'components/FiltersPopovers/ThemesFilterPopover/ThemesFilterPopover';
import { MapToggle } from 'components/MapToggleButton/MapToggleButton';
import { SortByDisplayContainer } from 'components/SortByDisplayContainer/SortByDisplayContainer';
import { useFiltersForm } from 'hooks/useFiltersForm';
import { getFacets } from 'modules/sapiSearch/selectors';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { FilterId } from '@daedalus/core/src/search/types';
import { ClearAllPill } from '../FiltersPill/ClearAllPill/ClearAllPill';
import { FacilitiesFilterPill } from '../FiltersPill/FacilitiesFilterPill/FacilitiesFilterPill';
import { PriceFilterPill } from '../FiltersPill/PriceFilterPill/PriceFilterPill';
import { StarRatingPill } from '../FiltersPill/StarRatingPill/StarRatingPill';
const filterKeys = getAllFilterKeys();
interface Props {
  isActive?: boolean;
}
const ALL_FILTERS: Record<FilterId, IQuickFilter<Props>> = {
  SortByDisplayContainer: {
    id: FilterId.SortByDisplayContainer,
    name: ['sortField', 'sortOrder'],
    Component: ({
      isActive
    }: Props) => <SortByDisplayContainer isActive={isActive} />
  },
  PriceFilterPill: {
    id: FilterId.PriceFilterPill,
    name: ['priceMin', 'priceMax'],
    Component: ({
      isActive
    }: Props) => <PriceFilterPill isActive={isActive} />
  },
  PropertyTypeFilterPill: {
    id: FilterId.PropertyTypeFilterPill,
    name: 'propertyTypes',
    Component: ({
      isActive
    }: Props) => <PropertyTypeFilterPill isActive={isActive} />
  },
  // Star Rating
  StarRatingFilterPopover: {
    id: FilterId.StarRatingFilterPopover,
    name: 'starRatings',
    Component: ({
      isActive
    }: Props) => <StarRatingFilterPopover isActive={isActive} />
  },
  StarRatingPill: {
    id: FilterId.StarRatingPill,
    name: 'starRatings',
    Component: ({
      isActive
    }: Props) => <StarRatingPill isActive={isActive} />
  },
  StarRatingQuickPill: {
    id: FilterId.StarRatingQuickPill,
    name: 'starRatings',
    activeCondition: {
      name: 'starRatings',
      value: ['4', '5']
    },
    Component: ({
      isActive
    }: Props) => <StarRatingPill quickFilterValues={['4', '5']} isActive={isActive} isQuickFilter />
  },
  ReviewScorePill: {
    id: FilterId.ReviewScorePill,
    name: 'reviewScore',
    Component: ({
      isActive
    }: Props) => <ReviewScorePill isActive={isActive} />
  },
  FacilitiesFilterPill: {
    id: FilterId.FacilitiesFilterPill,
    name: 'facilities',
    Component: ({
      isActive
    }: Props) => <FacilitiesFilterPill isActive={isActive} />
  },
  FreeCancellationPill: {
    id: FilterId.FreeCancellationPill,
    name: 'freeCancellation',
    Component: ({
      isActive
    }: Props) => <FreeCancellationPill isActive={isActive} />
  },
  PayLaterPill: {
    id: FilterId.PayLaterPill,
    name: 'payLater',
    Component: ({
      isActive
    }: Props) => <PayLaterPill isActive={isActive} />
  },
  PriceFilterPopover: {
    id: FilterId.PriceFilterPopover,
    name: ['priceMin', 'priceMax'],
    Component: ({
      isActive
    }: Props) => <PriceFilterPopover isActive={isActive} />
  },
  FacilitiesFilterPopover: {
    id: FilterId.FacilitiesFilterPopover,
    name: 'facilities',
    Component: ({
      isActive
    }: Props) => <FacilitiesFilterPopover isActive={isActive} />
  },
  ReviewScoreFilterPopover: {
    id: FilterId.ReviewScoreFilterPopover,
    name: 'reviewScore',
    Component: ({
      isActive
    }: Props) => <ReviewScoreFilterPopover isActive={isActive} />
  },
  ThemesFilterPopover: {
    id: FilterId.ThemesFilterPopover,
    name: 'themes',
    Component: ({
      isActive
    }: Props) => <ThemesFilterPopover isActive={isActive} />
  },
  PropertyTypeFilterPopover: {
    id: FilterId.PropertyTypeFilterPopover,
    name: 'propertyTypes',
    Component: ({
      isActive
    }: Props) => <PropertyTypeFilterPopover isActive={isActive} />
  },
  MealPlansFilterPopover: {
    id: FilterId.MealPlansFilterPopover,
    name: 'amenities',
    Component: ({
      isActive
    }: Props) => <MealsFilterPopover isActive={isActive} />
  },
  BreakfastPill: {
    id: FilterId.BreakfastPill,
    name: 'amenities',
    activeCondition: {
      name: 'amenities',
      value: 'breakfast'
    },
    Component: ({
      isActive
    }: Props) => <BreakfastPill isActive={isActive} />
  },
  ReviewScoreQuickPill: {
    id: FilterId.ReviewScoreQuickPill,
    name: 'reviewScore',
    activeCondition: {
      name: 'reviewScore',
      value: '8'
    },
    Component: ({
      isActive
    }: Props) => <ReviewScorePill isActive={isActive} isQuickFilter />
  },
  PetFriendlyPill: {
    id: FilterId.PetFriendlyPill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '8'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="8" />
  },
  SwimmingPoolPill: {
    id: FilterId.SwimmingPoolPill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '7'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="7" />
  },
  ParkingPill: {
    id: FilterId.ParkingPill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '6'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="6" />
  },
  BeachPill: {
    id: FilterId.BeachPill,
    name: 'themes',
    activeCondition: {
      name: 'themes',
      value: '1'
    },
    Component: ({
      isActive
    }: Props) => <ThemeQuickFilterPill isActive={isActive} themeId="1" />
  },
  FamilyPill: {
    id: FilterId.FamilyPill,
    name: 'themes',
    activeCondition: {
      name: 'themes',
      value: '9'
    },
    Component: ({
      isActive
    }: Props) => <ThemeQuickFilterPill isActive={isActive} themeId="9" />
  },
  FreeWifiPill: {
    id: FilterId.FreeWifiPill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '299'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="299" />
  },
  RestaurantPill: {
    id: FilterId.RestaurantPill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '5'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="5" />
  },
  AirConditionedPill: {
    id: FilterId.AirConditionedPill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '21'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="21" />
  },
  AirportShuttlePill: {
    id: FilterId.AirportShuttlePill,
    name: 'facilities',
    activeCondition: {
      name: 'facilities',
      value: '9'
    },
    Component: ({
      isActive
    }: Props) => <FacilityQuickFilterPill isActive={isActive} facilityId="9" />
  }
};
const QUICK_FILTERS = [ALL_FILTERS.FreeCancellationPill, ALL_FILTERS.PayLaterPill, ALL_FILTERS.StarRatingQuickPill, ALL_FILTERS.ReviewScoreQuickPill, ALL_FILTERS.SwimmingPoolPill, ALL_FILTERS.ParkingPill, ALL_FILTERS.PetFriendlyPill, ALL_FILTERS.BeachPill, ALL_FILTERS.FamilyPill, ALL_FILTERS.FreeWifiPill, ALL_FILTERS.RestaurantPill, ALL_FILTERS.AirConditionedPill, ALL_FILTERS.AirportShuttlePill];
const MOBILE_FILTERS_PILLS: [IQuickFilter<Props>[], IQuickFilter<Props>[]] = [[ALL_FILTERS.SortByDisplayContainer, ALL_FILTERS.PriceFilterPill], [ALL_FILTERS.PropertyTypeFilterPill, ALL_FILTERS.BreakfastPill, ALL_FILTERS.ReviewScorePill, ALL_FILTERS.FacilitiesFilterPill, ALL_FILTERS.StarRatingPill]];
const DESKTOP_FILTERS_PILLS: [IQuickFilter<Props>[], IQuickFilter<Props>[]] = [[ALL_FILTERS.PriceFilterPopover], [ALL_FILTERS.FacilitiesFilterPopover, ALL_FILTERS.MealPlansFilterPopover, ALL_FILTERS.ReviewScoreFilterPopover, ALL_FILTERS.ThemesFilterPopover, ALL_FILTERS.PropertyTypeFilterPopover, ALL_FILTERS.StarRatingFilterPopover]];
const _exp = /*#__PURE__*/() => Flex.Row;
const Wrapper = /*#__PURE__*/styled(_exp())({
  name: "Wrapper",
  class: "w1eivoi2",
  propsAsIs: true
});
const _exp9 = /*#__PURE__*/() => Flex.Row;
const FiltersContainer = /*#__PURE__*/styled(_exp9())({
  name: "FiltersContainer",
  class: "f19oe5id",
  propsAsIs: true
});
export const QuickFilters = () => {
  const {
    isMobile,
    isDesktopXs,
    isDesktopSm
  } = useDeviceLayout();
  const facets = useSelector(getFacets);
  const {
    brandEnabledFilters
  } = useBrand();
  const {
    getAppliedFilters,
    getFiltersCount,
    calculateIfFiltersChanged,
    getFilters
  } = useFiltersForm(filterKeys);
  const appliedFilters = getAppliedFilters();
  const count = getFiltersCount();
  const element: React.Ref<HTMLDivElement> = useRef(null);
  const allFiltersButton: React.Ref<HTMLDivElement> = useRef(null);
  const leftArrowLength = allFiltersButton?.current?.offsetWidth > 0 ? allFiltersButton?.current?.offsetWidth + 30 : 122;
  const filtersChanged = useMemo(() => calculateIfFiltersChanged(getFilters()), [getFilters, calculateIfFiltersChanged]);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const showMapToggle = useMemo(() => isDesktopXs || isDesktopSm, [isDesktopXs, isDesktopSm]);
  const filters = useMemo(() => {
    const baseFilters = isMobile ? MOBILE_FILTERS_PILLS : DESKTOP_FILTERS_PILLS;
    const isQuickFilterValid = (filter: IQuickFilter<Props>) => {
      if (!['facilities', 'themes'].includes(filter?.activeCondition?.name)) {
        return true;
      }
      return isFacetValid(filter.activeCondition.value as string, filter.activeCondition.name === 'facilities' ? 'facilities' : 'themeIds', facets);
    };
    const allFilters = [...baseFilters[0], ...QUICK_FILTERS.filter(isQuickFilterValid).slice(0, 8), ...baseFilters[1]];
    return brandEnabledFilters?.length ? allFilters.filter(filter => brandEnabledFilters.includes(filter.id)) : allFilters;
  }, [isMobile, brandEnabledFilters, facets]);
  const handleScrollToFirst = () => {
    setTimeout(() => {
      if (element.current) {
        element.current.scrollLeft = 0;
      }
    }, 0);
  };
  useEffect(() => {
    setActiveFilters(prevFilters => getOrderedActiveQuickFilters(filters, appliedFilters, prevFilters));
    handleScrollToFirst();
  }, [count, filtersChanged, filters]);
  const RenderFilters = () => <>
      {/* Render active filters */}
      {activeFilters.map(id => filters.find(filter => filter.id === id)).filter(Boolean).map(({
      id,
      Component
    }) => <Component key={id} isActive />)}

      {/* Render non-active filters */}
      {filters.filter(filter => !isQuickFilterActive(filter, appliedFilters)).map(({
      id,
      Component
    }) => <Component key={id} />)}
      <ClearAllPill />
    </>;
  if (isMobile) return <Wrapper justifyContent="space-between">
        <FiltersContainer componentRef={element} gap="s200" alignItems="center">
          <MobileFiltersButton /> <RenderFilters />
        </FiltersContainer>
      </Wrapper>;
  return <Wrapper justifyContent="space-between">
      <FiltersContainer componentRef={element} gap="s200" alignItems="center">
        <div ref={allFiltersButton}>
          <AllFiltersOverlay />
        </div>
        <FiltersScrollWrapper right={showMapToggle ? 170 : 0} left={leftArrowLength}>
          <RenderFilters />
        </FiltersScrollWrapper>
      </FiltersContainer>
      {showMapToggle && <MapToggle />}
    </Wrapper>;
};

require("./QuickFilters.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./QuickFilters.tsx");